import {
	MessageType,
	NotificationType,
	ReceivedMessage,
	WebSocketOptions,
} from '../types';
import { WS_CLOSE_INTENTIONAL } from '../constants/common';

class WebSocketService {
	private socket: WebSocket | null = null;
	private isConnected: boolean = false;
	private options: WebSocketOptions;
	messageQueue: string[] = [];

	constructor(options: WebSocketOptions) {
		this.options = options;
		this.connect();
	}

	private trace(message: string, ...args: unknown[]): void {
		this.options.debugLog?.(message, ...args);
	}

	private safeWsUrl(): string {
		const u = this.options.baseUrlWs;
		const q = u.indexOf('?');
		return q === -1 ? u : `${u.slice(0, q)}?…`;
	}

	private getWebSocketConstructor(): typeof WebSocket {
		if (typeof WebSocket === 'undefined') {
			throw new Error(
				'Global WebSocket is required. Use a Node.js version with built-in WebSocket support.',
			);
		}
		return WebSocket;
	}

	private socketUrl(): string {
		const auth = this.options.authKeys?.apiKey
			? `apiKey=${encodeURIComponent(this.options.authKeys?.apiKey)}`
			: this.options.authKeys?.token
				? `auth=${encodeURIComponent(this.options.authKeys.token)}`
				: '';

		return `${this.options.baseUrlWs}?${auth}`;
	}

	private connect() {
		const Ws = this.getWebSocketConstructor();
		this.socket = new Ws(this.socketUrl());

		this.trace('ws connecting', this.safeWsUrl());

		this.socket.addEventListener('open', () => {
			this.isConnected = true;
			const queued = [...this.messageQueue];
			this.messageQueue = [];

			if (this.options.onOpen) {
				this.options.onOpen(deliveries => {
					const toSend = [...new Set([...deliveries, ...queued])];
					if (toSend.length) {
						this.send(toSend);
					}
				});
			} else if (queued.length) {
				this.send(queued);
			}

			this.trace('ws open');
		});

		this.socket.addEventListener('close', event => {
			this.trace(
				'ws close',
				JSON.stringify({
					code: event.code,
					reason: event.reason,
					wasClean: event.wasClean,
				}),
			);
			this.isConnected = false;
			if (this.options.onClose) {
				this.options.onClose(event);
			}
		});

		this.socket.addEventListener('message', event => {
			const notification = this.getMessage(event);
			this.trace('ws message received', notification);
			this.options.onMessage(notification);
		});

		this.socket.addEventListener('error', event => {
			if (this.options.onError) {
				this.options.onError({ type: NotificationType.Error, data: event });
			}
			this.trace('ws transport error', event);
		});
	}

	send(deliveryIds: string[]) {
		if (this.isConnected && this.socket) {
			deliveryIds.forEach(deliveryId => {
				const wire = JSON.stringify({
					type: MessageType.SentMessage,
					payload: { command: `delivery.listen`, args: deliveryId },
				});
				this.socket && this.socket.send(wire);
				this.trace('ws message sent', wire);
			});
		} else {
			deliveryIds.forEach(deliveryId => {
				!this.messageQueue.includes(deliveryId) &&
					this.messageQueue.push(deliveryId);
			});
			this.trace(
				'ws send queued (not connected)',
				{
					wouldSend: deliveryIds.map(id =>
						JSON.stringify({
							type: MessageType.SentMessage,
							payload: { command: `delivery.listen`, args: id },
						}),
					),
				},
				'queueLength',
				this.messageQueue.length,
			);
		}
	}

	close() {
		if (this.socket) {
			this.isConnected = false;
			const readyState = this.socket.readyState;

			this.trace('ws service close invoked', 'readyState', readyState);

			if (
				readyState === WebSocket.OPEN ||
				readyState === WebSocket.CONNECTING ||
				readyState === 0 ||
				readyState === 1
			) {
				this.socket.close(WS_CLOSE_INTENTIONAL);
			}
			this.messageQueue = [];
			this.socket = null;
		}
	}

	getMessage(event: MessageEvent) {
		const newMessage: ReceivedMessage = event.data
			? JSON.parse(event.data)
			: {};
		return newMessage.type === MessageType.Error
			? {
					type: NotificationType.Error as const,
					data: { ...newMessage.payload, requested: newMessage.requested },
				}
			: {
					type: NotificationType.Message as const,
					data: newMessage.payload,
				};
	}
}

export default WebSocketService;
